import type { RequestOptions } from '@@/plugin-request/request';
import { getLocale, type RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import cookie from './utils/cookie';
import nProgress from 'nprogress';
import React from 'react';

// Hata işleme türleri
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// Sunucu tarafıyla anlaşılan yanıt veri yapısı
export interface ResponseStructure {
  success: boolean;
  error: ErrorType;
}

interface ErrorType {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: ValidationExceptionType[];
  instance: any;
}

interface ValidationExceptionType {
  property?: string;
  messages?: string[];
}

/**
 * @name Hata İşleme
 * Projenin varsayılan hata işleme mekanizması, burada özelleştirme yapılabilir.
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // Hata işleme: umi@3'ün hata işleme mekanizması.
  errorConfig: {
    // Hata fırlatma
    // errorThrower http 2xx dönen sonuçları işlemek için kullanılır.
    // 2xx dönmüş fakat success === false ise errorThrower ile işlenir.
    errorThrower: (res) => {
      nProgress.done();
      const { success, message } = res as unknown as API.IResult;
      if (!success) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = { error };
        throw error; // Kendi oluşturduğumuz hatayı fırlat
      }
    },
    // Hata yakalama ve işleme
    // errorHandler http istekleri 4xx ve 5xx dönen sonuçları işlemek için kullanılır.
    // ayrıca errorThrower ile fırlatılan hataları da burada işleyebiliriz.
    errorHandler: (error: any, opts: any) => {
      nProgress.done();
      if (opts?.skipErrorHandler) throw error;
      // Bizim errorThrower ile fırlatılan hata.
      // request success 200 döndürülmüş fakat success nesnesi false ise
      if (error.name === 'BizError') {
        const errorInfo = error.info;
        if (errorInfo) {
          const { error } = errorInfo;
          message.error({ key: 'bizError', content: error.detail || error.message });
        }
      } else if (error.response) {
        // Axios hatası
        // İstek başarıyla gönderildi ve sunucu da bir durum kodu ile yanıt verdi, ancak durum kodu 2xx aralığının dışında
        if (error.response.data as ResponseStructure) {
          const response = error.response.data as unknown as ResponseStructure;
          const { error: errorDetails } = response;
          const { title, detail, errors, status } = errorDetails;
          if (!errors || errors.length === 0) {
            // Eğer errors yoksa sadece detail göster
            // message.warning({
            //   key: status,
            //   content: detail || 'An error occurred during the operation!',
            //   duration: 5,
            // });
            notification.warning({
              message: title || 'Warning',
              description: detail || 'An error occurred during the operation!',
              type: 'warning',
              key: status,
              duration: 5,
              placement: 'top'
            });
          } else {
            // Eğer errors varsa detaylı hata mesajları göster
            errors.forEach((err) => {
              notification.open({
                message: title || 'Error',
                description: React.createElement(
                  'div',
                  null,
                  ...(err.messages || []).map((msg, index) =>
                    React.createElement(
                      'span',
                      { key: index },
                      `- ${msg}`,
                      React.createElement('br'),
                    ),
                  ),
                ),
                type: 'warning',
              });
            });
          }
        }
      } else if (error.request) {
        // İstek başarıyla gönderildi, ancak yanıt alınamadı
        // \`error.request\` tarayıcıda XMLHttpRequest örneğidir,
        // ve node.js'de http.ClientRequest örneğidir
        message.error({ key: 'error.request', content: 'Yanıt alınamadı! Lütfen tekrar deneyin.' });
      } else {
        // İstek gönderilirken bir sorun oluştu
        message.error({ key: 'requestError', content: 'İstek hatası, lütfen tekrar deneyin.' });
      }
    },
  },

  // İstek onaylayıcıları (interceptors)
  requestInterceptors: [
    (config: RequestOptions) => {
      nProgress.start();
      let headers: any = {
        'Accept-Language': getLocale(),
      };

      const user = cookie.checkTokenIfIsValid();

      if (user) {
        headers.accept = 'application/json';
        headers.Authorization = `Bearer ${cookie.getAccessToken()}`;
      }

      return { ...config, headers };
    },
  ],

  // Yanıt onaylayıcıları (interceptors)
  // request yapısı success === false olanları kendi işliyor bu yüzden aşağısı sadece success === true olanları işliyor.
  // Eğer success === false ise yukarıdaki errorHandler'dan işleniyor.
  responseInterceptors: [
    (response) => {
      nProgress.done();
      return response;
    },
  ],
};

// Sadece mesajı (ör: detail) döndüren sade error handler fonksiyonu
export function extractErrorMessage(error: any): string | undefined {
  // BizError ise
  if (error.name === 'BizError') {
    const errorInfo = error.info;
    if (errorInfo) {
      const { error: err } = errorInfo;
      return err.detail || err.message;
    }
  } else if (error.response) {
    // Axios hatası
    if (error.response.data as ResponseStructure) {
      const response = error.response.data as unknown as ResponseStructure;
      const { error: errorDetails } = response;
      const { detail, errors } = errorDetails || {};
      if (errors && errors.length > 0) {
        // errors varsa tüm mesajları birleştirip döndür
        return errors
          .map((err: { messages?: string[] }) => (err.messages || []).join('\n'))
          .filter(Boolean)
          .join('\n');
      }
      return detail || 'An error occurred during the operation!';
    }
  } else if (error.request) {
    return 'Yanıt alınamadı! Lütfen tekrar deneyin.';
  } else {
    return 'İstek hatası, lütfen tekrar deneyin.';
  }
  return undefined;
}
